import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrencyUtils } from "../../utils/currencyUtils";
import { requestVehicleReturn } from "../../services/vehicleServices.ts";
import placeholder from "../../assets/Car-place-holder-PNG.png";
import { FaEye, FaEdit } from "react-icons/fa";

// --- START: Modal Component (No changes needed here) ---
const ConfirmationModal = ({ isOpen, onClose, onConfirm, vehicleName }) => {
  if (!isOpen) return null;

  return (
    // Fixed overlay/backdrop for the modal
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      aria-modal="true"
      role="dialog"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Request Vehicle Return
        </h3>
        <p className="text-gray-700 mb-6">
          Are you sure you want to request the return of
          <span className="font-bold text-red-600"> "{vehicleName}"</span>? This
          action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
          >
            Request
          </button>
        </div>
      </div>
    </div>
  );
};
// --- END: Modal Component ---

const VehicleListCard = ({ vehicle }) => {
  const {
    id, // <--- This is the vehicleId you need to use
    title,
    slug,
    lotNo,
    vin,
    miles,
    cylinder,
    fuelType,
    engine,
    startBidding,
    currentBidding,
    image,
    sellerName = "",
    highlights = [],
    ProductStatus,
    rejectionReason = "",
    auctionName = "",
    reservedPrice = 0,
  } = vehicle;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();

  /**
   * CORRECTED: Made the function async to use await, and added error handling.
   * Passes the destructured 'id' as the vehicleId.
   */
  const handleConfirmReturn = async () => {
    // Close the modal immediately for better user experience
    setIsModalOpen(false);

    try {
      // 1. Pass the correct ID variable (`id`) to the service function
      const response = await requestVehicleReturn({ vehicleId: id });

      // OPTIONAL: Add success feedback here (e.g., a toast notification)
      console.log("Return request submitted successfully:", response);

      // OPTIONAL: You might want to refresh the list or update the status locally
      // e.g., onSuccessfulReturn();
    } catch (error) {
      // 2. Handle API error (e.g., show an error toast/message)
      console.error("Error requesting vehicle return:", error);
      // OPTIONAL: Show a user-friendly error message
      // e.g., alert('Failed to request return. Please try again.');
    }
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-4 items-center px-2 py-1 hover:bg-default transition-colors duration-200">
        {/* ... (Existing Vehicle Card Content) ... */}

        {/* Vehicle Image */}
        <div className="col-span-1">
          <img
            src={image || placeholder}
            alt={title}
            className="w-16 h-12 object-cover rounded"
            onError={(e) => {
              e.target.src = placeholder;
            }}
          />
        </div>
        {/* Vehicle Info */}
        <div className="col-span-3">
          <h3 className="font-semibold text-white text-sm truncate mb-1">
            {title}
          </h3>
          <div className="text-xs text-white/90 mb-1">
            Lot: <span className="font-medium">{lotNo}</span>
          </div>
          <div className="text-xs text-white/90 truncate mb-2">
            VIN: {vin ? `${vin}` : "N/A"}
          </div>

          {/* Highlights */}
          {highlights.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {highlights.slice(0, 2).map((highlight, i) => {
                const isObject =
                  typeof highlight === "object" && highlight !== null;
                const name = isObject ? highlight.name : highlight;
                const bgColor = isObject ? highlight.bgColor : "#f97316";
                const textColor = isObject ? highlight.textColor : "#fff";

                return (
                  <span
                    key={i}
                    className="px-2 py-[2px] rounded text-[10px] font-medium shadow-sm"
                    style={{
                      backgroundColor: bgColor,
                      color: textColor,
                    }}
                  >
                    {name}
                  </span>
                );
              })}
              {highlights.length > 2 && (
                <span className="bg-gray-600 text-white px-2 py-[2px] rounded text-[10px] font-medium shadow-sm">
                  +{highlights.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
        {/* Specs or Rejection Reason */}
        {(ProductStatus === "Under Approval" || ProductStatus === "Sold") && (
          <div className="col-span-1 text-xs text-white text-left">
            <span className="font-medium">
              {displayPriceWithSymbol(
                convertPrice(Number(vehicle.soldPrice || 0)),
              )}
            </span>
          </div>
        )}
        {ProductStatus !== "Under Approval" && ProductStatus !== "Sold" && (
          <div className="col-span-1 text-xs text-white text-left">
            <span className="font-medium">
              {displayPriceWithSymbol(
                convertPrice(Number(vehicle.reservedPrice || 0)),
              )}
            </span>
          </div>
        )}
        {ProductStatus === "Rejected" ? (
          <>
            <div className="col-span-5 text-sm text-red-500 text-left">
              <span className="font-medium text-sm">
                {rejectionReason || "-"}
              </span>
            </div>
            {/* Actions inline with rejection reason */}
            <div className="col-span-2 flex items-center justify-end gap-2">
              {/* View Button */}
              <div className="relative h-10 w-10">
                <button
                  onClick={() => navigate(`/${slug}`)}
                  className="absolute right-0 top-0 h-10 w-10 hover:w-32 flex items-center bg-secondary-dark border border-default text-white rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group"
                >
                  <div className="flex items-center h-full w-full">
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                      <FaEye size={18} />
                    </div>
                    <span className="pr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs font-medium沟通 whitespace-nowrap">
                      View Details
                    </span>
                  </div>
                </button>
              </div>

              {/* Edit Button */}
              <div className="relative h-10 w-10">
                <button
                  onClick={() => navigate(`/addvehicles?edit=${id}`)}
                  className="absolute right-0 top-0 h-10 w-10 hover:w-32 flex items-center bg-gradient-to-r from-primary to-secondary border border-default text-white rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group"
                >
                  <div className="flex items-center h-full w-full">
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                      <FaEdit size={18} />
                    </div>
                    <span className="pr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs font-medium whitespace-nowrap">
                      Edit Vehicle
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="col-span-1 text-xs text-white text-left">
              <span className="font-medium">
                {miles?.toLocaleString() || "-"}
              </span>
            </div>
            <div className="col-span-1 text-xs text-white text-left">
              <span className="font-medium">{fuelType || "-"}</span>
            </div>
            <div className="col-span-1 text-xs text-white text-left">
              <span className="font-medium">{cylinder || "-"}</span>
            </div>
            <div className="col-span-1 text-xs text-white text-left">
              <span className="font-medium">{engine || "-"}</span>
            </div>
            <div className="col-span-1 text-xs text-white text-left">
              <span className="font-medium">
                {vehicle.updatedAt
                  ? new Date(vehicle.updatedAt).toLocaleDateString()
                  : "-"}
              </span>
            </div>
            {vehicle.showAuctionColumn && (
              <div className="col-span-1 text-xs text-white text-left">
                <span className="font-medium">{auctionName || "-"}</span>
              </div>
            )}
            <div className="col-span-1"></div>
            {/* Actions */}
            <div className="col-span-1 flex items-end justify-end gap-2">
              <div className="relative h-10 w-10">
                <button
                  onClick={() => navigate(`/${slug}`)}
                  className="absolute right-0 top-0 h-10 w-10 hover:w-32 flex items-center bg-secondary-dark border border-default text-white rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group"
                >
                  <div className="flex items-center h-full w-full">
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                      <FaEye size={18} />
                    </div>
                    <span className="pr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs font-medium沟通 whitespace-nowrap">
                      View Details
                    </span>
                  </div>
                </button>
              </div>
              {/* Request Return Button */}
              {ProductStatus === "Stock" && (
                <div>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="px-3 py-1.5 text-xs border border-primary text-white bg-primary rounded hover:bg-primary/80 transition-colors duration-200"
                  >
                    Request Return
                  </button>
                </div>
              )}
              {/* Edit button */}
              {(ProductStatus === "Waiting" ||
                ProductStatus?.name === "Waiting" ||
                ProductStatus === "Awaiting" ||
                ProductStatus?.name === "Awaiting") &&
                !(
                  ProductStatus === "Awaiting Arrival" ||
                  ProductStatus?.name === "Awaiting Arrival"
                ) && (
                  <div className="relative h-10 w-10">
                    <div className="relative h-10 w-10">
                      <button
                        onClick={() => navigate(`/addvehicles?edit=${id}`)}
                        className="absolute right-0 top-0 h-10 w-10 hover:w-32 flex items-center bg-gradient-to-r from-primary to-secondary border border-default text-white rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group"
                      >
                        <div className="flex items-center h-full w-full">
                          <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                            <FaEdit size={18} />
                          </div>
                          <span className="pr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs font-medium whitespace-nowrap">
                            Edit Vehicle
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmReturn} // Calls the async function
        vehicleName={title}
      />
    </>
  );
};

export default VehicleListCard;
